import { lazy } from 'react';
import { RouteObject } from 'react-router-dom';

// Lazy load pages
const Home = lazy(() => import('../pages/home/page'));
const NotFound = lazy(() => import('../pages/NotFound'));
const Login = lazy(() => import('../pages/login/page'));
const Signup = lazy(() => import('../pages/signup/page'));
const ForgotPassword = lazy(() => import('../pages/forgot-password/page'));
const Dashboard = lazy(() => import('../pages/dashboard/page'));
const Invoice = lazy(() => import('../pages/dashboard/invoice/page'));
const Schedule = lazy(() => import('../pages/dashboard/schedule/page'));
const Team = lazy(() => import('../pages/dashboard/team/page'));
const GPSTracking = lazy(() => import('../pages/dashboard/gps-tracking/page'));
const Contact = lazy(() => import('../pages/contact/page'));
const Pricing = lazy(() => import('../pages/pricing-calculator/page'));
const Demo = lazy(() => import('../pages/demo/page'));
const Blog = lazy(() => import('../pages/blog/page'));
const BlogDetail = lazy(() => import('../pages/blog-detail/page'));
const BestToolsTradieInvoicing2026 = lazy(() => import('../pages/blog/best-tools-tradie-invoicing-2026/page'));
const HowTradiesGetQuotesFaster = lazy(() => import('../pages/blog/how-tradies-get-quotes-faster/page'));
const PrivacyPolicy = lazy(() => import('../pages/privacy-policy/page'));
const TermsOfService = lazy(() => import('../pages/terms-of-service/page'));
const HelpCenter = lazy(() => import('../pages/help-center/page'));
const Resources = lazy(() => import('../pages/resources/page'));
const ResourcesLibrary = lazy(() => import('../pages/resources-library/page'));
const PromoDeals = lazy(() => import('../pages/promo-deals/page'));
const ProAddons = lazy(() => import('../pages/pro-addons/page'));
const ThankYou = lazy(() => import('../pages/thank-you/page'));
const InvoicePage = lazy(() => import('../pages/invoice/page'));
const InvoiceDemo = lazy(() => import('../pages/invoice-demo/page'));
const GPSTrackingPage = lazy(() => import('../pages/gps-tracking/page'));
const GoogleAds = lazy(() => import('../pages/google-ads/page'));
const SoleTrader = lazy(() => import('../pages/sole-trader/page'));
const JobMarketplace = lazy(() => import('../pages/job-marketplace/page'));

// Industry pages
const Plumbing = lazy(() => import('../pages/industries/plumbing/page'));
const Electrical = lazy(() => import('../pages/industries/electrical/page'));
const Carpentry = lazy(() => import('../pages/industries/carpentry/page'));
const HVAC = lazy(() => import('../pages/industries/hvac/page'));
const Painting = lazy(() => import('../pages/industries/painting/page'));
const Roofing = lazy(() => import('../pages/industries/roofing/page'));
const Landscaping = lazy(() => import('../pages/industries/landscaping/page'));
const Tiling = lazy(() => import('../pages/industries/tiling/page'));

// Video landing pages
const VideoLanding60SecondOverview = lazy(() => import('../pages/video-landing/60-second-overview/page'));
const VideoLandingGPSTracking = lazy(() => import('../pages/video-landing/gps-tracking/page'));
const VideoLandingQuoteToPayment = lazy(() => import('../pages/video-landing/quote-to-payment/page'));
const VideoLandingTradieTestimonial = lazy(() => import('../pages/video-landing/tradie-testimonial/page'));
const VideoLandingWhyTradiesSwitch = lazy(() => import('../pages/video-landing/why-tradies-switch/page'));

const TestimonialsPage = lazy(() => import('../pages/testimonials/page'));

const DashboardCompanyJobsPage = lazy(() => import('../pages/dashboard/company/jobs/page'));
const DashboardCompanyInvoicesPage = lazy(() => import('../pages/dashboard/company/invoices/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Home />,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/signup',
    element: <Signup />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/dashboard',
    element: <Dashboard />,
  },
  {
    path: '/dashboard/invoice',
    element: <Invoice />,
  },
  {
    path: '/dashboard/schedule',
    element: <Schedule />,
  },
  {
    path: '/dashboard/team',
    element: <Team />,
  },
  {
    path: '/dashboard/gps-tracking',
    element: <GPSTracking />,
  },
  {
    path: '/dashboard/company/worker-upload',
    lazy: async () => {
      const Component = (await import('../pages/dashboard/company/worker-upload/page')).default;
      return { Component };
    }
  },
  {
    path: '/dashboard/company/approvals',
    lazy: async () => {
      const Component = (await import('../pages/dashboard/company/approvals/page')).default;
      return { Component };
    }
  },
  {
    path: '/dashboard/company/jobs',
    element: <DashboardCompanyJobsPage />
  },
  {
    path: '/dashboard/company/invoices',
    element: <DashboardCompanyInvoicesPage />
  },
  {
    path: '/contact',
    element: <Contact />,
  },
  {
    path: '/pricing',
    element: <Pricing />,
  },
  {
    path: '/demo',
    element: <Demo />,
  },
  {
    path: '/blog',
    element: <Blog />,
  },
  {
    path: '/blog/:slug',
    element: <BlogDetail />,
  },
  {
    path: '/blog/best-tools-tradie-invoicing-2026',
    element: <BestToolsTradieInvoicing2026 />,
  },
  {
    path: '/blog/how-tradies-get-quotes-faster',
    element: <HowTradiesGetQuotesFaster />,
  },
  {
    path: '/privacy-policy',
    element: <PrivacyPolicy />,
  },
  {
    path: '/terms-of-service',
    element: <TermsOfService />,
  },
  {
    path: '/help-center',
    element: <HelpCenter />,
  },
  {
    path: '/resources',
    element: <Resources />,
  },
  {
    path: '/resources-library',
    element: <ResourcesLibrary />,
  },
  {
    path: '/promo-deals',
    element: <PromoDeals />,
  },
  {
    path: '/pro-addons',
    element: <ProAddons />,
  },
  {
    path: '/thank-you',
    element: <ThankYou />,
  },
  {
    path: '/invoice',
    element: <InvoicePage />,
  },
  {
    path: '/invoice-demo',
    element: <InvoiceDemo />,
  },
  {
    path: '/gps-tracking',
    element: <GPSTrackingPage />,
  },
  {
    path: '/google-ads',
    element: <GoogleAds />,
  },
  {
    path: '/sole-trader',
    element: <SoleTrader />,
  },
  {
    path: '/job-marketplace',
    element: <JobMarketplace />,
  },
  {
    path: '/industries/plumbing',
    element: <Plumbing />,
  },
  {
    path: '/industries/electrical',
    element: <Electrical />,
  },
  {
    path: '/industries/carpentry',
    element: <Carpentry />,
  },
  {
    path: '/industries/hvac',
    element: <HVAC />,
  },
  {
    path: '/industries/painting',
    element: <Painting />,
  },
  {
    path: '/industries/roofing',
    element: <Roofing />,
  },
  {
    path: '/industries/landscaping',
    element: <Landscaping />,
  },
  {
    path: '/industries/tiling',
    element: <Tiling />,
  },
  {
    path: '/video/60-second-overview',
    element: <VideoLanding60SecondOverview />,
  },
  {
    path: '/video/gps-tracking',
    element: <VideoLandingGPSTracking />,
  },
  {
    path: '/video/quote-to-payment',
    element: <VideoLandingQuoteToPayment />,
  },
  {
    path: '/video/tradie-testimonial',
    element: <VideoLandingTradieTestimonial />,
  },
  {
    path: '/video/why-tradies-switch',
    element: <VideoLandingWhyTradiesSwitch />,
  },
  {
    path: '/testimonials',
    element: <TestimonialsPage />,
  },
  {
    path: '/airtasker-service',
    lazy: async () => {
      const Component = (await import('../pages/airtasker-service/page')).default;
      return { Component };
    }
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;